import { useParams } from "react-router";
import { cloneDeep, isEmpty, isEqual } from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  useCheckDuplicatedTitle,
  usePropertiesPatch,
  usePropertyById,
} from "../../../hooks/properties";
import useServiceRequestTopButtons from "../../../hooks/useServiceRequestTopButtons";
import useReportTopButtons from "../../../hooks/useReportTopButtons";
import useQueryNotFoundNavigation from "../../../hooks/navigation/useQueryNotFoundNavigation";
import usePropertyFormReducer from "../../../hooks/usePropertyFormReducer";
import { useGetTags } from "../../../hooks/useTags";

const usePropertyViewData = () => {
  const { propertyId } = useParams();

  const { data: property, isLoading, error } = usePropertyById(propertyId);

  const [editedProperty, dispatch] = usePropertyFormReducer();

  // Redirect to 404 page if resource is not found
  useQueryNotFoundNavigation({ error });

  // Hook to calculate buttons for SR tab
  const { getRequestButtonProps } = useServiceRequestTopButtons();

  // Hook to calculate buttons for Report tab
  const { getReportButtonProps } = useReportTopButtons();

  const { mutate: patchProperty, mutateAsync: patchPropertyAsync } =
    usePropertiesPatch();

  // async function and state to check duplicated property title
  const { fetchPropertyByTitle, titleExists } = useCheckDuplicatedTitle();

  const { data: tagsData, isLoading: isFetchingTags } = useGetTags();
  const [editing, setEditing] = useState(false);
  const [tmpAvatarImg, setTmpAvatarImg] = useState({});

  // set Property avatar
  useEffect(() => {
    if (!isEmpty(tmpAvatarImg))
      dispatch({
        type: "addImage",
        image: tmpAvatarImg,
      });
  }, [tmpAvatarImg, dispatch]);

  // take a copy of original edited Property to validate changes
  const origEditedProperty = useMemo(
    () =>
      cloneDeep({
        ...property,
        title: property?.title.toLowerCase(),
        currentTags: isFetchingTags
          ? []
          : property?.tags?.map((tag) => {
              const { label, reference } = tagsData?.tagsDict?.[tag] || {};
              return { label, value: reference };
            }),
        originalResource: { ...property },
      }),
    [property, tagsData?.tagsDict, isFetchingTags]
  );

  // check if editedProperty has been modified
  const isPropertyEdited = useMemo(() => {
    const sameProperty = isEqual(origEditedProperty, {
      ...editedProperty,
      title: editedProperty?.title?.toLowerCase(),
    });
    return !sameProperty;
  }, [editedProperty, origEditedProperty]);

  // required fields validation
  const requiredFieldsFilled =
    Boolean(editedProperty?.title) &&
    Boolean(editedProperty?.timezone) &&
    Boolean(editedProperty?.propertyType);

  // validation for unique name
  const titleIsUnique =
    property?.title?.toLowerCase() === editedProperty?.title?.toLowerCase()
      ? true
      : !titleExists;

  const disableSaveBtn =
    !editing || titleIsUnique
      ? !isPropertyEdited || !requiredFieldsFilled
      : !titleIsUnique;

  return {
    editing,
    titleIsUnique,
    disableSaveBtn,
    isPropertyEdited,
    titleExists,
    propertyId,
    property,
    isLoading,
    editedProperty,
    tmpAvatarImg,
    setEditing,
    setTmpAvatarImg,
    getRequestButtonProps,
    getReportButtonProps,
    patchProperty,
    patchPropertyAsync,
    fetchPropertyByTitle,
    dispatch,
  };
};

export default usePropertyViewData;
